/**
 * Lease Management API Service
 *
 * Comprehensive API for managing tenant leases including termination,
 * room switches, extensions, and term updates.
 */

import {post} from './client';
import CryptoJS from "crypto-js";
import {KEY} from "../../Constants";

const BASE_PATH = 'pegasus/visionary/lease-management';

// ==================== LEASE DETAILS ====================

const getOriginalText = () => {
    const encryptedData = localStorage.getItem("myMap");
    return encryptedData
        ? CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8)
        : "";
};

export const leaseManagementApi = {
    /**
     * Get comprehensive lease details
     * @param {Object} params
     * @param {number} params.lease_id - Lease ID
     * @param {number} params.landlord_id - Landlord ID
     * @returns {Promise<Object>} Lease details with financial summary
     */
    getLeaseDetails: async ({lease_id, landlord_id}) => {
        const originalText = getOriginalText();

        return post(`${BASE_PATH}/details/get`, {
            lease_id,
            landlord_id
        }, {
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            }

        });
    },

    /**
     * Get tenant lease history (all past and present leases)
     * @param {Object} params
     * @param {number} params.tenant_id - Tenant ID
     * @param {number} params.landlord_id - Landlord ID
     * @returns {Promise<Object>} Lease history with summaries
     */
    getLeaseHistory: async ({tenant_id, landlord_id}) => {
        const originalText = getOriginalText();

        return post(`${BASE_PATH}/history/get`, {
            tenant_id,
            landlord_id
        }, {
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            }

        });
    },

    /**
     * Terminate an active lease
     * @param {Object} params
     * @param {number} params.lease_id - Lease ID
     * @param {number} params.landlord_id - Landlord ID
     * @param {string} params.termination_reason - Reason for termination
     * @param {string} params.vacate_date - ISO 8601 date string
     * @param {boolean} params.refund_deposit - Whether to process deposit refund
     * @returns {Promise<Object>} Termination result with deposit details
     */
    terminateLease: async ({
                               lease_id,
                               landlord_id,
                               termination_reason,
                               vacate_date,
                               refund_deposit = false
                           }) => {
        const originalText = getOriginalText();

        return post(`${BASE_PATH}/terminate`, {
            lease_id,
            landlord_id,
            termination_reason,
            vacate_date,
            refund_deposit
        }, {
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            }

        });
    },

    /**
     * Switch tenant to a different room/unit
     * @param {Object} params
     * @param {number} params.lease_id - Current lease ID
     * @param {number} params.landlord_id - Landlord ID
     * @param {number} params.new_property_id - New property ID
     * @param {string} params.new_house_number - New unit number
     * @param {number} params.new_monthly_rent - New rent amount
     * @param {string} params.switch_date - ISO 8601 date string
     * @param {boolean} params.transfer_arrears - Transfer unpaid arrears
     * @param {boolean} params.billing_enabled - Enable billing for new lease
     * @returns {Promise<Object>} Room switch result
     */
    switchRoom: async ({
                           lease_id,
                           landlord_id,
                           new_property_id,
                           new_house_number,
                           new_monthly_rent,
                           switch_date,
                           transfer_arrears = false,
                           billing_enabled = true
                       }) => {
        const originalText = getOriginalText();

        return post(`${BASE_PATH}/switch-room`, {
            lease_id,
            landlord_id,
            new_property_id,
            new_house_number,
            new_monthly_rent,
            switch_date,
            transfer_arrears,
            billing_enabled
        }, {
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            }

        });
    },

    /**
     * Extend lease end date
     * @param {Object} params
     * @param {number} params.lease_id - Lease ID
     * @param {number} params.landlord_id - Landlord ID
     * @param {string} params.new_end_date - New end date (ISO 8601)
     * @param {number} params.new_monthly_rent - Optional new rent
     * @param {string} params.reason - Optional reason for extension
     * @returns {Promise<Object>} Extension result
     */
    extendLease: async ({
                            lease_id,
                            landlord_id,
                            new_end_date,
                            new_monthly_rent,
                            reason
                        }) => {
        const originalText = getOriginalText();

        const payload = {
            lease_id,
            landlord_id,
            new_end_date
        };

        if (new_monthly_rent) payload.new_monthly_rent = new_monthly_rent;
        if (reason) payload.reason = reason;

        return post(`${BASE_PATH}/extend`, payload, {
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            }

        });
    },

    /**
     * Update lease terms (partial update supported)
     * @param {Object} params
     * @param {number} params.lease_id - Lease ID
     * @param {number} params.landlord_id - Landlord ID
     * @param {number} params.monthly_rent - Optional new rent
     * @param {number} params.deposit_held - Optional new deposit
     * @param {boolean} params.billing_enabled - Optional billing status
     * @param {number} params.water_units - Optional water reading
     * @param {number} params.electricity_units - Optional electricity reading
     * @param {string} params.lease_start_date - Optional start date
     * @param {string} params.lease_end_date - Optional end date
     * @returns {Promise<Object>} Update result with changes
     */
    updateLeaseTerms: async (params) => {
        const originalText = getOriginalText();

        // Only send non-null fields
        const payload = Object.entries(params).reduce((acc, [key, value]) => {
            if (value !== null && value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {});

        return post(`${BASE_PATH}/update-terms`, payload, {
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            }

        });
    }
};

export default leaseManagementApi;
